<template>
  <van-popup v-model:show="showModal" :closeable="false" round class="w-full">
    <div class="card bg-base-100 shadow-xl">
      <div class="card-body text-center">
        <!-- 标题 -->
        <h3
          class="card-title text-lg font-bold text-base-content mb-4 justify-center"
        >
          支付中
        </h3>

        <!-- 加载动画 -->
        <div class="flex justify-center mb-6">
          <span class="loading loading-spinner loading-lg text-primary"></span>
        </div>

        <!-- 提示文字 -->
        <p class="text-base-content/80 mb-6">正在处理您的支付，请稍候...</p>

        <!-- 取消按钮 -->
        <div class="card-actions justify-center">
          <button @click="handleCancel" class="btn btn-outline btn-wide">
            取消支付
          </button>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { readMemberSubscriptionPage } from "@/apis/user";
import { ref, watch, onUnmounted, computed } from "vue";

const props = defineProps<{
  show: boolean;
  subscriptionId: number;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "cancel"): void;
  (e: "success"): void;
  (e: "failed", error: string): void;
}>();

const showModal = computed({
  get: () => props.show,
  set: (value) => emit("update:show", value),
});

// 轮询相关
const pollingInterval = ref<NodeJS.Timeout | null>(null);
const maxPollingTime = 5 * 60 * 1000; // 最大轮询时间 5分钟
const pollingFrequency = 3 * 1000; // 轮询频率 3 秒

// 取消支付
const handleCancel = () => {
  stopPolling();
  emit("cancel");
  emit("update:show", false);
};

// 开始轮询
const startPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
  }

  let elapsedTime = 0;

  pollingInterval.value = setInterval(async () => {
    elapsedTime += pollingFrequency;

    try {
      // Mock 支付状态检查
      const res = await readMemberSubscriptionPage();
      let paymentStatus = "";

      if (res.page.data?.some((item) => item.id === props.subscriptionId)) {
        paymentStatus = "success";
      }

      if (paymentStatus === "success") {
        stopPolling();
        emit("success");
      } else if (paymentStatus === "failed") {
        stopPolling();
        emit("failed", "支付失败");
      }
      // pending 状态继续轮询
    } catch (error) {
      console.error("轮询支付状态失败:", error);
      // 继续轮询，不中断
    }

    // 超时处理
    if (elapsedTime >= maxPollingTime) {
      stopPolling();
      emit("failed", "支付超时");
    }
  }, pollingFrequency);
};

// 停止轮询
const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

// 监听show变化，开始/停止轮询
watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      startPolling();
    } else {
      stopPolling();
    }
  }
);

// 组件卸载时清理
onUnmounted(() => {
  stopPolling();
});
</script>
